import { Badge, MenuItem, SvgIcon, SvgIconProps, Tooltip, Typography } from '@chronosphereio/chrono-ui';
import { Select, SelectProps } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { SupportedDataType } from '@/core';
import { PluginMetadata, PluginType } from '../../model';
import { useVizFrameworkConfig } from '../../model/config';
import { doesPluginSupportDataType, useListPluginMetadata, usePlugins } from '../../runtime';

export interface PluginKindSelectProps extends Omit<SelectProps<string>, 'children'> {
  pluginType: PluginType;
  validDataType?: SupportedDataType;
}

const EMPTY_PLUGINS: PluginMetadata[] = [];

/*
  Gets the icons for the plugins.  Since icons currently only exist on panel plugins,
  we force the type to be 'panel' so any non panel plugins will be excluded
  @param plugins - The list of plugins
  @returns The icons for the plugins.
*/
const usePanelPluginIcons = (plugins: PluginMetadata[]) => {
  const panelPlugins = plugins.filter((plugin) => plugin.pluginType === 'Panel');
  const pluginImplementationQueryResults = usePlugins('Panel', panelPlugins);

  const someLoading = pluginImplementationQueryResults.some((result) => result.isLoading);

  return useMemo(() => {
    if (someLoading) {
      return {};
    }

    return plugins.reduce(
      (iconRegistry, plugin, index) => {
        const result = pluginImplementationQueryResults[index];
        if (result !== undefined && result.data !== undefined) {
          iconRegistry[plugin.kind] = result.data.icon;
        }

        return iconRegistry;
      },
      {} as Record<string, ((props: SvgIconProps) => ReactNode) | undefined>
    );
  }, [pluginImplementationQueryResults, someLoading, plugins]);
};

/**
 * Displays a MUI Select input for selecting a plugin's kind from a list of all the available plugins of a specific
 * plugin type. (e.g. "Show a list of all the Panel plugins" or "Show a list of all the Variable plugins").
 */
export function PluginKindSelect(props: PluginKindSelectProps) {
  const { pluginType, value: propValue, validDataType, ...others } = props;
  const { data, isLoading } = useListPluginMetadata(pluginType);
  const config = useVizFrameworkConfig();

  // Pass an empty value while options are still loading so MUI doesn't complain about us using an "out of range" value
  const value = propValue !== '' && isLoading ? '' : propValue;

  const pluginIconRegistry = usePanelPluginIcons(data ?? []);

  const hasIcons = Object.values(pluginIconRegistry).some((icon) => icon !== undefined);

  // Filter out any plugins that have the "hidden" flag set to true
  const visiblePlugins = useMemo(() => {
    return (
      data?.filter((plugin) => {
        const showPlugin = plugin.display.hidden !== true;
        // Hide the plugin if it is not marked as "hidden" and the user has not selected it
        if (!showPlugin && value !== plugin.kind) {
          return false;
        }

        if (plugin.display.isMock && !config?.enableVizFrameworkMocks) {
          // Do not show mock plugins if they are not configured to be enabled.
          return false;
        }

        if (config?.filterPlugins) {
          // If a filterPlugins function is provided via config, use it to filter the plugins
          return config.filterPlugins(plugin.kind);
        }

        return true;
      }) ?? EMPTY_PLUGINS
    );
  }, [data, config, value]);

  const renderIcon = useMemo(() => {
    return function PluginIcon(kind: string) {
      return pluginIconRegistry[kind] ? (
        pluginIconRegistry[kind]?.({ fontSize: 'small' })
      ) : (
        // If no icon is provided, render a placeholder icon to keep spacing consistent
        <SvgIcon fontSize="small" />
      );
    };
  }, [pluginIconRegistry]);

  const menuItems = useMemo(() => {
    return visiblePlugins.map((metadata: PluginMetadata) => {
      // Do not disable the plugin if it is selected, even if it doesn't support the data type.
      // Otherwise, the select component will display an empty value.
      const isPluginSelected = metadata.kind === value;
      const disabled = !doesPluginSupportDataType({ validDataType, plugin: metadata }) && !isPluginSelected;

      const menuItem = (
        <MenuItem key={metadata.kind} value={metadata.kind} disabled={disabled}>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasIcons ? renderIcon(metadata.kind) : null}

            <span>
              {metadata.display.name} <PluginKindSelectItemBadge metadata={metadata} />
            </span>
          </Typography>
        </MenuItem>
      );

      return disabled ? (
        <Tooltip key={metadata.kind} Content="Datasource not compatible with panel type" placement="right">
          <span>{menuItem}</span>
        </Tooltip>
      ) : (
        menuItem
      );
    });
  }, [visiblePlugins, validDataType, value, hasIcons, renderIcon]);

  return (
    <Select sx={{ minWidth: 120 }} {...others} value={value}>
      {menuItems}
    </Select>
  );
}

type PluginKindSelectItemBadgeProps = {
  metadata: PluginMetadata;
};

export function PluginKindSelectItemBadge({ metadata }: PluginKindSelectItemBadgeProps) {
  if (metadata.display.isPreview) {
    return <Badge sx={{ ml: 1 }} variant="new" label="Preview" size="small" />;
  }

  if (metadata.display.isMock) {
    return (
      <Tooltip Content="Mock plugins are internal-only examples used to document how plugins work, show example behavior, and enable testing.">
        <Badge sx={{ ml: 1 }} variant="default" label="Mock" size="small" />
      </Tooltip>
    );
  }

  // No badge needed
  return null;
}
